import {
  Component,
  h,
  Prop,
  Host,
  Element,
  forceUpdate,
  State,
  Event,
  EventEmitter,
} from '@stencil/core';
import {
  BreakpointCustomizable,
  injectCSS,
  setAriaAttributes,
  observeAttributes,
  injectFont,
  observeProperties,
  attachSlottedCss,
  createUuid,
  TValidationState,
  Autocomplete,
  AutocompleteConfig,
} from '../../utils';
import {
  addInputEventListenerForSearch,
  isInsideForm,
  TInputClearEvent,
  TInputLabelPosition,
} from './input-utils';
import { createStyles, getSlottedCss } from './input-styles';
import { handleButtonEvent } from '../../utils/functions/buttonHandling';
import { StateMessage } from '../common/state-message/state-message';

@Component({
  tag: 'dc-ui-input-wrapper',
  shadow: true,
})
export class DcUiInputWrapper {
  @Element() el: HTMLElement;

  @Prop() label: string;
  @Prop() labelPosition: TInputLabelPosition = 'default';
  @Prop() labelWidth?: BreakpointCustomizable<string> = '25%';
  @Prop() message?: string = '';
  @Prop() validationstate?: TValidationState = 'none';
  @Prop() hideLabel: BreakpointCustomizable<boolean>;
  @Prop() filedrop?: boolean;

  //Autocomplete-related props
  @Prop() autocomplete?: false;
  @Prop() autocompleteUrl?: string;
  @Prop() autocompleteParams?: string;
  @Prop() autocompleteDefaultQuery?: string;
  @Prop() autocompleteMaxResults?: number = 1;
  @Prop() autocompleteHighlightResults?: boolean;

  @State() private isClearable = false;

  // Fire event when input is cleared
  @Event({ bubbles: false }) clear: EventEmitter<TInputClearEvent>;

  private isFile: boolean;
  private isSearch: boolean;
  private isDate: boolean;
  private isInsideForm: boolean;

  //Autocomplete props
  private isAutocomplete: boolean;
  private autocompleteOptions?: AutocompleteConfig;
  private autocompleteInstance?: Autocomplete;
  private isValidApiUrl: boolean;

  private input: HTMLInputElement;
  labelEl: HTMLLabelElement;

  public componentWillLoad(): void {
    this.autocompleteParams = this.autocompleteParams?.trim();
    injectFont();

    const forbiddenTypes = [
      'button',
      'checkbox',
      'color',
      'datetime-local',
      'image',
      'month',
      'radio',
      'range',
      'reset',
      'submit',
      'time',
      'week',
    ];

    const allowedAutocompleteTypes = ['search', 'text'];

    const input = this.el.querySelector('input');
    if (!input) {
      throw new Error('No input element found');
    }

    if (!forbiddenTypes.includes(input.type)) {
      this.input = input;
    }

    this.isFile = this.input.type === 'file';
    this.isSearch = this.input.type === 'search';
    this.isInsideForm = isInsideForm(this.el);
    this.isDate = this.input.type === 'date';
    this.isAutocomplete =
      this.autocomplete && allowedAutocompleteTypes.indexOf(this.input.type) >= 0;

    if (this.isSearch) {
      this.isClearable = !!this.input.value;
      observeProperties(this.input, ['value'], () => (this.isClearable = !!this.input.value));
    }

    this.observeAttributes(); // once initially
  }

  public componentDidLoad(): void {
    if (this.isSearch) {
      addInputEventListenerForSearch(this.input, (hasValue) =>
        (this.isClearable = hasValue));
    }

    if (this.isAutocomplete) {
      this.isValidApiUrl = this.validateApiUrl();
      this.initAutocomplete();
    }

    if (this.isAutocomplete) {
      this.isValidApiUrl = this.validateApiUrl();
      this.initAutocomplete();
    }
  }

  public connectedCallback(): void {
    attachSlottedCss(this.el, getSlottedCss(this.el, this.labelPosition));
    this.observeAttributes(); // on every reconnect
  }

  public componentDidRender(): void {
    setAriaAttributes(this.input, {
      label: this.label,
      message: this.message,
    });

    // check if textarea is not empty or has a placeholder and add focus class to label
    if (this.input.value !== '' || this.input.placeholder !== '') {
      this.labelEl.classList.add('focus');
    }

    // event on input focus
    if (this.isFile) {
      this.input.addEventListener('change', () => {
        const fileName = this.input.value.split('\\').pop();
        const fileContainer = this.el.shadowRoot.querySelector('.file-container');
        const fileNameEl = this.el.shadowRoot.querySelector('#file-name');

        if (fileName) {
          fileContainer.classList.add('file-selected');
          fileNameEl.textContent = fileName;
        } else {
          fileContainer.classList.remove('file-selected');
          fileNameEl.textContent = '';
        }
      });

      return;
    }

    this.input.addEventListener('focus', () => {
      this.labelEl.classList.add('focus');
    });

    // event on input blur if not datepicker
    if (!this.isDate) {
      this.input.addEventListener('blur', () => {
        // remove focus class from label if input is empty
        if (this.input.value === '' && this.input.placeholder === '')
          this.labelEl.classList.remove('focus');
      });
    }
  }

  private observeAttributes = (): void => {
    observeAttributes(this.input, ['disabled', 'readonly', 'required'], () => forceUpdate(this.el));
  };

  private onLabelClick = (): void => {
    this.input.focus();
  };

  private onFileButtonClick = (): void => {
    this.input.click();
  };

  private onClear = (): void => {
    this.input.value = '';
    this.input.removeAttribute('value');
    this.input.focus();
    this.clear.emit({ clear: true });
  };

  private onSubmit = (event: MouseEvent): void => {
    handleButtonEvent(
      event,
      this.el,
      () => 'submit',
      () => this.input.disabled
    );
  };

  private getInputId = (): string => {
    if (this.input.id) {
      return this.input.id;
    } else {
      // generate random id and set it to input
      const id = createUuid();
      this.input.id = id;
      return id;
    }
  };

  /**
   * Autocomplete init
   */
  private initAutocomplete() {
    if (this.input.disabled || this.input.readOnly || !this.isValidApiUrl) {
      return;
    }

    this.autocompleteOptions = {
      url: this.autocompleteUrl,
      minChars: 1,
      selector: 'label',
      resultListClass: 'suggestions-list',
      closeOnSelect: true,
      fetchOnFocus: true,
      defaultQuery: this.autocompleteDefaultQuery || '',
      searchTermPlaceholder: '{query}',
      highlightResults: this.autocompleteHighlightResults,
      ...(this.autocompleteParams && {
        additionalParameters: `&${this.autocompleteParams}`,
      }),
      onSelect: (selectedItem) => {
        this.input.value = selectedItem.name;
        this.labelEl.classList.add('focus');
      },
    };

    //Create new autocomplete instance and init it
    this.autocompleteInstance = new Autocomplete(
      this.autocompleteOptions,
      this.el, //set host
      this.input
    );
    this.autocompleteInstance.init();
  }

  /* URL Validation */
  private readonly validateApiUrl = (): boolean => {
    if (!this.autocompleteUrl) {
      return false;
    }

    try {
      new URL(this.autocompleteUrl);
      return true;
    } catch (error) {
      return false;
    }
  };

  disconnectedCallback() {
    this.autocompleteInstance?.destroy();
  }

  render() {
    injectCSS(
      this.el,
      createStyles,
      this.labelPosition,
      this.labelWidth,
      this.hideLabel,
      this.input.disabled,
      this.input.type,
      this.validationstate,
      this.input.readOnly,
      this.autocompleteMaxResults
    );

    return (
      <Host>
        {this.isFile && (
          <label>
            <dc-ui-button
              variant={this.validationstate === 'error' ? 'primary' : 'light'}
              disabled={this.input.disabled}
              onClick={this.onFileButtonClick}>
              {this.label} {this.input.required && ' *'}
            </dc-ui-button>
            <div class="file-container">
              <span id="file-name"></span>
            </div>
            <slot />
          </label>
        )}

        {!this.isFile && (
          <label
            class={{
              focus: this.isDate,
            }}
            ref={(label) => (this.labelEl = label)}
            htmlFor={this.getInputId()}>
            <div id="label-container">
              {this.label && (
                <span
                  id="label-text"
                  onClick={this.onLabelClick}>
                  {this.label} {this.input.required ? ' *' : ''}
                </span>
              )}
            </div>
            <div id="input-container">
              {this.isSearch && [
                this.isInsideForm ? (
                  <button
                    id="search-button"
                    onClick={this.onSubmit}
                    type="submit">
                    <dc-ui-icon
                      color="darkBlue02"
                      size="tiny"
                      name="dc_search"></dc-ui-icon>
                  </button>
                ) : (
                  <dc-ui-icon
                    id="search-icon"
                    color="darkBlue02"
                    size="tiny"
                    name="dc_search"></dc-ui-icon>
                ),
              ]}
              <slot />
              {this.isSearch && this.isClearable && (
                <button
                  onClick={this.onClear}
                  id="clear-search-button"
                  hidden={!this.isClearable}>
                  <dc-ui-icon
                    color="darkBlue02"
                    size="tiny"
                    name="dc_close"></dc-ui-icon>
                </button>
              )}
              {this.isDate && (
                <dc-ui-icon
                  id="calendar-icon"
                  name="dc_calendar"
                  color="darkBlue02"
                  size="tiny"></dc-ui-icon>
              )}
              {this.validationstate !== 'none' && (
                <StateMessage
                  state={this.validationstate}
                  message={this.message}></StateMessage>
              )}
            </div>
          </label>
        )}
      </Host>
    );
  }
}
